import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Form, Message } from 'semantic-ui-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import ExternalFacilityDataModal from './ExternalFacilityDataModal'
import ExternalFacilityDataTable from './ExternalFacilityDataTable'
import ParkingAuditLog from './ParkingAuditLog/ParkingAuditLog'
import ParkingSales from './ParkingSales'
import QueryContainer from '../../components/QueryContainer'
import VouchersTable from '../../components/VouchersTable'
import { filterArrayByDistinctProperty } from '../../services/utils'
import parkingService from '../../services/Parking'
import { MoovyButton, showLocalizedMoovyToast } from '../../components'
import MoovyModal from '../../components/MoovyModal'

const SalesContainer = ({ parkingRef, parking, operatorRealm }) => {
  const sales = useQuery({
    queryKey: ['sales', parkingRef],
    queryFn: () => parkingService.fetchSales(parkingRef, operatorRealm)
  })
  return (
    <QueryContainer query={sales}>
      <ParkingSales sales={sales.data ? sales.data : []} parking={parking} />
    </QueryContainer>
  )
}
const VouchersContainer = ({ parkingRef, operatorRealm, onClickVoucher }) => {
  const vouchers = useQuery({
    queryKey: ['vouchers', parkingRef],
    queryFn: () => parkingService.fetchUsedVouchers(parkingRef, operatorRealm)
  })
  return (
    <QueryContainer query={vouchers}>
      <VouchersTable
        vouchers={
          vouchers.data
            ? filterArrayByDistinctProperty(vouchers.data, 'ref')
            : []
        }
        onClickVoucher={onClickVoucher}
        compact
      />
    </QueryContainer>
  )
}

const ParkingAuditLogContainer = ({
  parkingRef,
  operatorRealm,
  isInsideParkingModal = false
}) => {
  const [commentModalOpen, setCommentModalOpen] = useState(false)
  const queryClient = useQueryClient()
  const intl = useIntl()

  const auditLogs = useQuery({
    queryKey: ['auditLog', parkingRef],
    queryFn: () => parkingService.fetchAuditLog(parkingRef, operatorRealm)
  })

  const { mutate, ...mutation } = useMutation({
    mutationFn: ({ comment }) =>
      parkingService.createAuditLogComment({
        parkingRef,
        comment,
        operatorRealm
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['auditLog', parkingRef]
      })
      setCommentModalOpen(false)
      showLocalizedMoovyToast(intl, {
        title: 'auditLogComment.toast.title',
        description: 'auditLogComment.toast.description'
      })
    }
  })

  const handleModalClose = () => {
    setCommentModalOpen(false)
    mutation.reset()
  }
  return (
    <QueryContainer query={auditLogs}>
      <>
        <MoovyButton
          onClick={() => setCommentModalOpen(true)}
          floated="right"
          style={{ marginBottom: '1rem' }}
        >
          <FormattedMessage id="auditLogComment.button" />
        </MoovyButton>
        <ParkingAuditLog auditLogs={auditLogs.data} />
        <AuditLogCommentModal
          open={commentModalOpen}
          onClose={handleModalClose}
          onSubmit={mutate}
          mutation={mutation}
          isInsideParkingModal={isInsideParkingModal}
        />
      </>
    </QueryContainer>
  )
}

const AuditLogCommentModal = ({
  open,
  onClose,
  onSubmit,
  mutation,
  isInsideParkingModal
}) => {
  const [comment, setComment] = useState('')
  const intl = useIntl()

  const handleSubmit = (evt) => {
    evt.preventDefault()
    onSubmit({ comment })
  }

  useEffect(() => {
    setComment('')
  }, [open])

  return (
    <MoovyModal
      open={open}
      size="mini"
      header={<FormattedMessage id="auditLogComment.header" />}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitButtonLabel={<FormattedMessage id="common.button.save" />}
      submitDisabled={comment.trim().length === 0}
      mutation={mutation}
      style={
        isInsideParkingModal
          ? { top: '50%', transform: 'translateY(-50%)' }
          : null
      }
    >
      <Form onSubmit={handleSubmit}>
        <Form.TextArea
          label={intl.formatMessage({
            id: 'modalChangeUserBlockList.addToBlocklist.comment'
          })}
          value={comment}
          maxLength="1000"
          onChange={(e, { value }) => {
            setComment(value)
          }}
          autoFocus
        />
      </Form>
    </MoovyModal>
  )
}

const ExternalFacilityDataContainer = ({ parkingRef, operatorRealm }) => {
  const query = useQuery({
    queryKey: ['externalFacilityData', parkingRef],
    queryFn: () =>
      parkingService.fetchExternalFacilityData(parkingRef, operatorRealm)
  })

  const [selected, setSelected] = useState(null)
  const [errorLoadingImage, setErrorLoadingImage] = useState(false)

  const onLoadingError = () => {
    setSelected(null)
    setErrorLoadingImage(true)
  }

  return (
    <QueryContainer query={query}>
      <>
        {errorLoadingImage && (
          <Message error>
            <FormattedMessage id="parkingModal.tab.externalFacilityDataContainer.imageLoadingError" />
          </Message>
        )}
        <ExternalFacilityDataTable
          data={query.data ? query.data : []}
          onRowClick={(row) => {
            setSelected(row)
            setErrorLoadingImage(false)
          }}
        />
        <ExternalFacilityDataModal
          externalDataRef={selected ? selected.moovyControlTransactionId : null}
          sessionRef={parkingRef}
          open={Boolean(selected)}
          onClose={() => setSelected(null)}
          onError={onLoadingError}
        />
      </>
    </QueryContainer>
  )
}

export default {
  SalesContainer,
  VouchersContainer,
  ParkingAuditLogContainer,
  ExternalFacilityDataContainer
}
